import { ImageLoading, InventoryTable, SingleSelectV3 } from "@common/component";
import AppText from "@common/component/AppText";
import MiscellaneousModal from "@common/component/MiscellaneousModal";
import MultiSelectDropDownFilterV2 from "@common/component/MultiSelectDropDownFilterV2";
import RangeSelect from "@common/component/RangeSelect/RangeSelect";
import { getSelectedWorkshop } from "@common/helper";
import { THEME_COLORS } from "@common/themes";
import { PurchaserContext, PurchaserInterface } from "@src/context/PurchaserContext";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import React, { useCallback, useContext, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Divider } from "react-native-elements";
import {
  fetchInventoryListing,
  fetchInventoryMetadata,
  InventoryListingItem,
  OutboundInventoryListing,
} from "../apiService";
import { DayRangeValue, FilterValue, MultiSelectValue, SelectValue } from "../commonTypes";
import { InventoryTypeEnum } from "../constants";
import { cityColumnData, workshopColumnData } from "./columnData";
import OutboundHistoryModal from "./OutboundHistoryModal";

const OutboundInventory = ({ navigation, search }) => {
  const inventoryType = InventoryTypeEnum.Outbound;
  const { workshopId, workshopType } = getSelectedWorkshop() ?? {};

  const { state: purchaserState, setMiscModal } = useContext<PurchaserInterface>(PurchaserContext);

  const [appliedSort, setAppliedSort] = useState<string>("");
  const [appliedFilters, setAppliedFilters] = useState<Record<string, FilterValue>>({});

  const [refurbIdForHistory, setRefurbIdForHistory] = useState<null | number>(null);

  const { data: metadata, isLoading: isLoadingMetadata } = useQuery({
    queryKey: ["outboundMeta", { workshopId }],
    queryFn: () => fetchInventoryMetadata({ workshopId, inventoryType }),
  });

  const {
    data: { listingData, totalCount } = { listingData: [], totalCount: 0 },
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["outboundListing", { workshopId, appliedFilters, appliedSort, search }],
    queryFn: ({ pageParam: pageNo }): Promise<OutboundInventoryListing> => {
      return fetchInventoryListing({
        workshopId,
        search,
        inventoryType,
        appliedFilters,
        appliedSort,
        pageNo,
        pageSize: 10,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: OutboundInventoryListing) =>
      lastPage.paginator_data.curr_page < lastPage.paginator_data.total_pages
        ? lastPage.paginator_data.curr_page + 1
        : // returning undefined indicates all pages has been loaded
          undefined,

    select: useCallback((data) => {
      return {
        listingData: data.pages.flatMap((it) => it.data),
        totalCount: data.pages.at(-1)?.paginator_data.count ?? 0,
      };
    }, []),
  });

  if (isLoadingMetadata) {
    return <ImageLoading />;
  }

  const renderFilters = () => {
    return Object.entries(metadata.filters).map(([filterKey, filter]) => {
      if (filter.type == "day-range") {
        const appliedFilterVal = (appliedFilters[filterKey] ?? { value: null, type: "day-range" }) as DayRangeValue;
        const value = appliedFilterVal.value;

        return (
          <RangeSelect
            label="Current task TAT"
            value={value}
            onChange={(val) => {
              setAppliedFilters((old) => ({
                ...old,
                [filterKey]: { ...appliedFilterVal, value: val },
              }));
            }}
          />
        );
      } else if (filter.type === "select") {
        if (!filter.items.length) {
          return null;
        }

        const appliedFilterVal = (appliedFilters[filterKey] ?? { value: "", type: "select" }) as SelectValue;
        const value = appliedFilterVal.value;

        return (
          <SingleSelectV3
            searchEnabled={filterKey === 'technician_or_inspector'}
            removeDefaultMargin
            key={filterKey}
            options={filter.items}
            value={value}
            setValue={(val) => {
              setAppliedFilters((old) => ({ ...old, [filterKey]: { ...appliedFilterVal, value: val } }));
            }}
            valueExtractor="key"
            labelExtractor="display_name"
            placeholder={filter.display_name}
            clearIconVisible
          />
        );
      } else if (filter.type === "multiselect") {
        if (!filter.items.length) {
          return null;
        }

        const appliedFilterVal = (appliedFilters[filterKey] ?? { value: [], type: "multiselect" }) as MultiSelectValue;
        const value = appliedFilterVal.value;

        return (
          <MultiSelectDropDownFilterV2
            hasSearch
            defaultTitle={filter.display_name}
            dropDownWidth={200}
            data={filter.items.map((it) => ({ id: it.key as unknown as string, title: it.display_name }))}
            setMiscModal={setMiscModal}
            selectedIndices={filter.items
              .map((it, idx) => ({ key: it.key, idx }))
              .filter((it) => appliedFilterVal.value.includes(it.key as unknown as string))
              .map((it) => it.idx)}
            selectedItem={filter.items
              .filter((it) => appliedFilterVal.value.includes(it.key as unknown as string))
              .map((it) => it.display_name)
              .join(", ")}
            onItemPress={(key) => {
              if (appliedFilterVal.value.includes(key)) {
                appliedFilterVal.value = appliedFilterVal.value.filter((it) => it !== key);
              } else {
                appliedFilterVal.value.push(key);
              }
              setAppliedFilters({ ...appliedFilters, [filterKey]: appliedFilterVal });
            }}
            onClearFilter={() => {
              appliedFilterVal.value = [];
              setAppliedFilters({ ...appliedFilters, [filterKey]: appliedFilterVal });
            }}
            canClearFilter={!!value && !!value.length}
          />
        );
      }
    });
  };

  const columnParams = {
    onClickHistoryIcon: (row: InventoryListingItem) => {
      setRefurbIdForHistory(row.outbound_car_details.outbound_refurb_car_id);
    },
  };

  const columnData = workshopType === "CITY_TEAM" ? cityColumnData(columnParams) : workshopColumnData(columnParams);

  return (
    <>
      <View style={styles.filtersContainer}>{renderFilters()}</View>
      <View style={styles.divider}>
        <AppText fontSize={14} fontFamily="bold">
          Total: {totalCount}
        </AppText>
        <Divider color={THEME_COLORS.sideMenuBg} style={{ flex: 1 }} width={4} />
      </View>
      <InventoryTable
        data={{ data: listingData ?? [] }}
        columnData={columnData}
        page={1}
        totalPages={10}
        onRowClick={(row: InventoryListingItem) => {
          navigation.navigate("CarRefurbMetrics", {
            refurbCarId: row.outbound_car_details.outbound_refurb_car_id,
            parent: "InventoryListingV3.1",
            metrics: "outbound",
          });
        }}
        rowProps={{ clickable: true }}
        loading={isFetchingNextPage || isLoading}
        loadMore={() => {
          fetchNextPage();
        }}
        onChangeSort={(orderBy, order) => {
          setAppliedSort(orderBy ? `${order === "desc" ? "-" : ""}${orderBy}` : "");
        }}
      />
      <OutboundHistoryModal
        open={!!refurbIdForHistory}
        refurbId={refurbIdForHistory}
        onClose={() => {
          setRefurbIdForHistory(null);
        }}
      />
      {purchaserState.miscModal.visibility ? (
        <MiscellaneousModal
          modalVisibility={purchaserState.miscModal.visibility}
          onClose={() => {
            setMiscModal(false, { x: -1, y: -1 }, null);
          }}
        />
      ) : null}
    </>
  );
};

export default OutboundInventory;

const styles = StyleSheet.create({
  filtersContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: 16,
    columnGap: 16,
  },
  divider: {
    // @ts-ignore
    marginTop: "1em",
    flexDirection: "row",
    alignItems: "center",
    columnGap: 16,
  },
});
